import React, { FunctionComponent, useMemo } from "react";
import styles from "./Carousel.module.scss";
import CarouselBlockProps from "./CarouselProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import getButtonStyles from "components/Button/helpers/getButtonStyles";
import { useSelector } from "react-redux";
import AliceCarousel from "react-alice-carousel";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import Slide from "./components/Slide/Slide";

const Carousel: FunctionComponent<CarouselBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const settings = useSelector((state: any) => state.settings.content);
    const themeButtonStyles = getButtonStyles(settings?.theme, props?.variant);

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const responsive = useMemo(
        () => ({
            0: { items: 1 },
            500: { items: 2 },
            568: { items: 3 },
            1024: { items: 4 },
        }),
        []
    );

    const items = useMemo(() => {
        return (props.content?.items ?? []).map((item) => (
            <Slide
                key={item.contentUdi}
                {...item}
            />
        ));
    }, [props.content?.items]);

    const hasItems = items.length > 0;
    const hasBackgroundMedia = Boolean(props.settings?.backgroundMedia?.[0]);

    return (
        <section
            className={combineClasses(styles.wrapper, inViewClasses, ...wrapperClasses)}
            style={{ ...wrapperStyles, ...splitStyles, ...themeButtonStyles }}
            ref={inViewElementRef}
        >
            <div className={combineClasses(styles.container, ...splitClasses)}>
                {/* Headline */}
                {props.content?.headline ? (
                    <DynamicHeading
                        key={`head_${props.contentUdi}`}
                        className={combineClasses(styles.headline, headlineCaseClass)}
                        text={props.content.headline}
                        headlineSize={props.settings.headlineSize}
                        headlineWeight={props.settings.headlineWeight}
                    />
                ) : null}

                <div className={styles.carousel}>
                    {hasItems ? (
                        <AliceCarousel
                            items={items}
                            paddingLeft={0}
                            paddingRight={50}
                            responsive={responsive}
                            infinite={true}
                            keyboardNavigation={true}
                            mouseTracking={true}
                            disableDotsControls={true}
                            disableButtonsControls={true}
                            autoPlay={!!props?.settings?.autoplay}
                            autoPlayInterval={props?.settings?.interval ?? 1000}
                            animationDuration={700}
                            animationType="fadeout"
                        />
                    ) : null}
                </div>
            </div>

            {hasBackgroundMedia ? (
                <BackgroundMedia
                    key={props.contentUdi}
                    {...props.settings.backgroundMedia![0]}
                />
            ) : null}
        </section>
    );
};

export default Carousel;
